import React, { useState } from 'react';
import { XIcon } from '@heroicons/react/solid';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';

const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

function StripePaymentModal({
  isOpen,
  onClose,
  onSuccess,
  amount,
  clientSecret,
  userEmail
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(userEmail || '');
  const [name, setName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [cardNumberFocus, setCardNumberFocus] = useState(false);
  const [cardExpiryFocus, setCardExpiryFocus] = useState(false);
  const [cardCvcFocus, setCardCvcFocus] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    const cardNumberElement = elements.getElement(CardNumberElement);

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: name,
            email: email,
            address: {
              postal_code: postalCode,
            },
          },
        },
        receipt_email: email,
      }
    );

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
    } else if (paymentIntent.status === 'succeeded') {
      setLoading(false);
      onSuccess(paymentIntent);
    }
  };

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto">
      <style>
        {`
          .stripe-input::placeholder {
            color: #aab7c4;
            opacity: 1;
          }
        `}
      </style>
      <div className="flex items-end justify-center min-h-screen sm:pt-4 sm:px-4 sm:pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white sm:rounded-lg px-4 pt-5 pb-4 text-left overflow-y-auto shadow-xl transform transition-all min-h-screen sm:min-h-0 w-full sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
              disabled={loading}
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg leading-6 font-bold text-gray-900 mb-6">
                Payment Details
              </h3>

              <form onSubmit={handleSubmit}>
                {/* Amount Display */}
                <div className="mb-6 bg-red-50 border-2 border-red-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Amount Due</span>
                    <span className="text-2xl font-bold text-primary">${amount.toFixed(2)}</span>
                  </div>
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="stripe-input w-full border border-gray-300 rounded-md p-3 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                    placeholder="your@email.com"
                  />
                  <p className="mt-1 text-xs text-gray-500">Receipt will be sent to this email</p>
                </div>

                {/* Cardholder Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="stripe-input w-full border border-gray-300 rounded-md p-3 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                    placeholder="Name on card"
                  />
                </div>

                {/* Card Number */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Number
                  </label>
                  <div className={`border rounded-md p-3 bg-white transition-all ${
                    cardNumberFocus
                      ? 'border-primary ring-1 ring-primary'
                      : 'border-gray-300'
                  }`}>
                    <CardNumberElement
                      options={ELEMENT_OPTIONS}
                      onFocus={() => setCardNumberFocus(true)}
                      onBlur={() => setCardNumberFocus(false)}
                    />
                  </div>
                </div>

                {/* Expiry and CVC */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date
                    </label>
                    <div className={`border rounded-md p-3 bg-white transition-all ${
                      cardExpiryFocus
                        ? 'border-primary ring-1 ring-primary'
                        : 'border-gray-300'
                    }`}>
                      <CardExpiryElement
                        options={ELEMENT_OPTIONS}
                        onFocus={() => setCardExpiryFocus(true)}
                        onBlur={() => setCardExpiryFocus(false)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CVC
                    </label>
                    <div className={`border rounded-md p-3 bg-white transition-all ${
                      cardCvcFocus
                        ? 'border-primary ring-1 ring-primary'
                        : 'border-gray-300'
                    }`}>
                      <CardCvcElement
                        options={ELEMENT_OPTIONS}
                        onFocus={() => setCardCvcFocus(true)}
                        onBlur={() => setCardCvcFocus(false)}
                      />
                    </div>
                  </div>
                </div>

                {/* Postal Code */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    required
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    maxLength={6}
                    className="stripe-input w-full border border-gray-300 rounded-md p-3 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                    placeholder="A1A1A1"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={!stripe || loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-red-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm"
                  >
                    {loading ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StripePaymentModal;